import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/QrCode2';
import VerifiedIcon from '@mui/icons-material/Verified';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';

function formatDate(value) {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString('pt-BR');
}

function formatMoney(value) {
  return Number(value || 59.9).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SubscriptionPaymentDialog({
  open,
  onClose,
  lanchoneteId,
  lanchoneteName,
  amount = 59.9,
  allowManualRelease = false,
  onSuccess
}) {
  const [loadingMethod, setLoadingMethod] = useState('');
  const [pixData, setPixData] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    stopPolling();
    setLoadingMethod('');
    setPixData(null);
    setReleasing(false);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleSuccess = (payload) => {
    stopPolling();
    toast.success('Pagamento confirmado! Assinatura atualizada.');
    onSuccess?.(payload);
    onClose();
  };

  const startPixPolling = (paymentId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/lanchonetes/${lanchoneteId}/subscription/payment/${paymentId}`);
        if (response.data?.status === 'approved') {
          handleSuccess(response.data);
        }
      } catch (error) {
        console.error('Erro ao consultar pagamento Pix:', error);
      }
    }, 4000);
  };

  const handlePay = async (method) => {
    if (!lanchoneteId) return;

    try {
      setLoadingMethod(method);
      const response = await api.post(`/lanchonetes/${lanchoneteId}/mercadopago-subscription-link`, { method });

      if (method === 'pix') {
        setPixData(response.data);
        if (response.data?.payment_id) {
          startPixPolling(response.data.payment_id);
        }
        return;
      }

      const checkoutUrl = response.data?.url;
      if (!checkoutUrl) {
        toast.error('Mercado Pago nao retornou o link de pagamento');
        return;
      }

      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      toast.info('Conclua o pagamento no Mercado Pago. O status sera atualizado automaticamente.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao iniciar pagamento');
    } finally {
      setLoadingMethod('');
    }
  };

  const handleCopyPix = async () => {
    if (!pixData?.qr_code) return;
    try {
      await navigator.clipboard.writeText(pixData.qr_code);
      toast.success('Codigo Pix copiado');
    } catch (error) {
      toast.error('Nao foi possivel copiar o codigo Pix');
    }
  };

  const handleManualRelease = async () => {
    if (!lanchoneteId) return;

    const confirmed = window.confirm(
      `Liberar a assinatura de "${lanchoneteName || 'esta lanchonete'}" manualmente?\n\nIsso vai contar como pagamento e atualizar o proximo vencimento.`
    );
    if (!confirmed) return;

    try {
      setReleasing(true);
      const response = await api.post(`/admin/lanchonetes/${lanchoneteId}/subscription/release`);
      toast.success(response.data?.message || 'Assinatura liberada com sucesso');
      onSuccess?.(response.data?.lanchonete || response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.details || error.response?.data?.error || 'Erro ao liberar assinatura');
    } finally {
      setReleasing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pagamento da mensalidade</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary">Lanchonete</Typography>
            <Typography fontWeight={700}>{lanchoneteName || `#${lanchoneteId}`}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Valor</Typography>
            <Typography variant="h5" fontWeight={800} color="primary.main">
              {formatMoney(amount)}
            </Typography>
          </Box>

          {!pixData ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={loadingMethod === 'pix' ? <CircularProgress size={18} color="inherit" /> : <PixIcon />}
                disabled={!!loadingMethod || releasing}
                onClick={() => handlePay('pix')}
              >
                Pagar com Pix
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={loadingMethod === 'card' ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
                disabled={!!loadingMethod || releasing}
                onClick={() => handlePay('card')}
              >
                Pagar com cartao
              </Button>
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Escaneie o QR Code ou copie o codigo Pix
              </Typography>
              {pixData.qr_code_base64 && (
                <Box
                  component="img"
                  src={`data:image/png;base64,${pixData.qr_code_base64}`}
                  alt="QR Code Pix"
                  sx={{ width: 220, height: 220, borderRadius: 2, border: '1px solid #e5e7eb', mb: 2 }}
                />
              )}
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyPix}
                disabled={!pixData.qr_code}
              >
                Copiar codigo Pix
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
                Aguardando confirmacao do pagamento...
              </Typography>
              {pixData.expires_at && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Expira em: {formatDate(pixData.expires_at)}
                </Typography>
              )}
            </Box>
          )}

          {allowManualRelease && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Liberacao manual (Admin Master)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Libera a lanchonete mesmo sem pagamento e registra como se tivesse pago hoje.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={releasing ? <CircularProgress size={18} /> : <VerifiedIcon />}
                  disabled={releasing || !!loadingMethod}
                  onClick={handleManualRelease}
                >
                  Liberar assinatura manualmente
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
